import { signUpPersonalPartnerPayloadState } from "@/atoms/user/sign-up-personal-partner-payload-state";
import { ButtonColor } from "@/common/enum/button-color";
import { LoginPath, SignUpPath } from "@/common/enum/path";
import { TextInputColor } from "@/common/enum/text-input-color";
import { validatePassword } from "@/common/validator/validate-password";
import { userService } from "@/services/user-service";
import { Button, TextInput } from "flowbite-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import validator from "validator";
import { CustomHelperText } from "../CustomHelperText";
import { PasswordEyeIcon } from "../icon/PasswordEyeIcon";
import { TextInputLabel } from "../label/TextInputLabel";
import { Body14Medium } from "../typography/Body14Medium";
import { Body14Underline } from "../typography/Body14Underline";

export const SignUpPersonalPartnerPanel = () => {
  const navigate = useNavigate();
  const [signUpPersonalPartnerPayload, setSignUpPersonalPartnerPayload] =
    useRecoilState(signUpPersonalPartnerPayloadState);
  const [confirmedPassword, setConfirmedPassword] = useState("");
  const [aggregateErrors, setAggregateErrors] = useState({
    email: "",
    password: "",
    confirmedPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmedPassword, setShowConfirmedPassword] = useState(false);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const toggleShowConfirmedPassword = () => {
    setShowConfirmedPassword(!showConfirmedPassword);
  };

  const isSignUpPersonalPartnerButtonActive =
    signUpPersonalPartnerPayload.email &&
    signUpPersonalPartnerPayload.password &&
    confirmedPassword;

  const handleSignUpPersonalPartnerPayloadChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    setSignUpPersonalPartnerPayload({
      ...signUpPersonalPartnerPayload,
      [name]: value,
    });
  };

  const handleConfirmedPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setConfirmedPassword(value);
  };

  const handleSignUpPersonalPartnerButtonClick = async () => {
    try {
      if (!validatePassword(signUpPersonalPartnerPayload.password)) {
        setAggregateErrors({
          ...aggregateErrors,
          password: "특수문자, 영어, 숫자 조합으로 8~15자를 입력해주세요.",
        });
        return;
      }

      if (!validator.isEmail(signUpPersonalPartnerPayload.email)) {
        setAggregateErrors({
          ...aggregateErrors,
          email: "이메일 형식이 올바르지 않습니다.",
        });
        return;
      }

      if (signUpPersonalPartnerPayload.password !== confirmedPassword) {
        setAggregateErrors({
          ...aggregateErrors,
          confirmedPassword: "비밀번호가 일치하지 않습니다.",
        });
        return;
      }

      const response = await userService.validateEmail(
        signUpPersonalPartnerPayload.email
      );

      if (response.ok) {
        navigate(SignUpPath.SIGN_UP_BUSINESS);
      } else {
        setAggregateErrors({
          ...aggregateErrors,
          email: "이미 가입된 아이디 입니다.",
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleLoginClick = async () => {
    navigate(LoginPath.LOGIN);
  };

  return (
    <div className="flex flex-col w-full">
      <TextInputLabel required>아이디</TextInputLabel>
      <TextInput
        required
        placeholder="aqara@gmail.com"
        name="email"
        value={signUpPersonalPartnerPayload.email}
        onChange={handleSignUpPersonalPartnerPayloadChange}
        color={
          !!aggregateErrors.email ? TextInputColor.FAILURE : TextInputColor.GRAY
        }
      />
      {!!aggregateErrors.email && (
        <CustomHelperText
          isError={!!aggregateErrors.email}
          text={aggregateErrors.email}
        />
      )}
      <div className="mt-[16px]">
        <TextInputLabel required>비밀번호</TextInputLabel>
      </div>
      <TextInput
        required
        placeholder="비밀번호를 입력해주세요"
        name="password"
        type={showPassword ? "text" : "password"}
        rightIcon={() => {
          return (
            <PasswordEyeIcon
              showPassword={showPassword}
              isError={!!aggregateErrors.password}
              onClick={toggleShowPassword}
            />
          );
        }}
        color={
          !!aggregateErrors.password
            ? TextInputColor.FAILURE
            : TextInputColor.GRAY
        }
        value={signUpPersonalPartnerPayload.password}
        onChange={handleSignUpPersonalPartnerPayloadChange}
      />
      <CustomHelperText
        isError={!!aggregateErrors.password}
        text={
          aggregateErrors.password ||
          "특수문자, 영어, 숫자 조합으로 8~15자를 입력해주세요."
        }
      />
      <div className="mt-[16px]">
        <TextInputLabel required>비밀번호 확인</TextInputLabel>
      </div>
      <TextInput
        required
        className={!aggregateErrors.confirmedPassword ? "mb-[32px]" : ""}
        placeholder="비밀번호를 한 번 더 입력해주세요"
        type={showConfirmedPassword ? "text" : "password"}
        rightIcon={() => {
          return (
            <PasswordEyeIcon
              showPassword={showConfirmedPassword}
              isError={!!aggregateErrors.confirmedPassword}
              onClick={toggleShowConfirmedPassword}
            />
          );
        }}
        color={
          !!aggregateErrors.confirmedPassword
            ? TextInputColor.FAILURE
            : TextInputColor.GRAY
        }
        value={confirmedPassword}
        onChange={handleConfirmedPasswordChange}
      />
      {!!aggregateErrors.confirmedPassword && (
        <CustomHelperText
          isError={!!aggregateErrors.confirmedPassword}
          text={aggregateErrors.confirmedPassword}
          className="mb-[32px]"
        />
      )}
      <Button
        className="mb-[24px]"
        color={isSignUpPersonalPartnerButtonActive && ButtonColor.PRIMARY}
        disabled={!isSignUpPersonalPartnerButtonActive}
        onClick={handleSignUpPersonalPartnerButtonClick}
      >
        다음
      </Button>
      <div className="flex text-center justify-center">
        <Body14Medium className="text-semantic-text-heading">
          아이디가 있으신가요?
        </Body14Medium>
        <div className="cursor-pointer ml-[4px]" onClick={handleLoginClick}>
          <Body14Underline>로그인 하기</Body14Underline>
        </div>
      </div>
    </div>
  );
};
